import { useEffect, useState } from "react";
import { Hotel, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { getTcSession } from "./HotelProviderSearch";

/* "Did you book this hotel?" — return prompt after an outbound provider click.
   On Yes: signed-in users pick a trip and the reservation is prefilled from the click. */
export default function DidYouBook() {
  const { user } = useAuth();
  const [click, setClick] = useState(null);
  const [choosing, setChoosing] = useState(false);
  const [trips, setTrips] = useState(null);
  const [conf, setConf] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/travel/clicks/recent?session_id=${getTcSession()}`)
      .then((r) => setClick((r.data.clicks || [])[0] || null))
      .catch(() => {});
  }, []);

  if (!click) return null;

  const answer = async (a) => {
    setBusy(true);
    try {
      await api.post(`/travel/clicks/${click.id}/booked?session_id=${getTcSession()}`, { answer: a });
      if (a === "yes" && user) {
        setChoosing(true);
        const r = await api.get("/trips").catch(() => ({ data: [] }));
        setTrips(r.data.items || r.data || []);
      } else {
        if (a === "yes") toast.success("Great! Sign in to save the reservation to a trip.");
        setClick(null);
      }
    } finally { setBusy(false); }
  };

  const saveTo = async (tid) => {
    try {
      await api.post(`/travel/trips/${tid}/stays`, {
        hotel_name: click.property_name || `Stay in ${click.destination}`,
        destination: click.destination, checkin: click.checkin, checkout: click.checkout,
        provider: click.provider_name, confirmation: conf || null,
        status: "confirmed", click_id: click.id,
      });
      toast.success("Reservation saved to your trip", {
        action: { label: "Open trip", onClick: () => window.location.assign(`/trips/${tid}`) },
      });
      setClick(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  return (
    <div className="border border-teal-300 bg-teal-50 p-4 mb-6" data-testid="did-you-book-banner">
      <div className="flex items-start gap-2">
        <Hotel className="w-5 h-5 text-teal-700 mt-0.5 shrink-0" />
        <div className="flex-1">
          {!choosing ? (
            <>
              <div className="text-sm font-700 text-teal-900">
                Did you book your stay in {click.destination}
                {click.provider_name ? ` on ${click.provider_name}` : ""}?
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <button onClick={() => answer("yes")} disabled={busy} data-testid="dyb-yes"
                  className="px-3 py-1.5 bg-teal-700 text-white text-xs font-700 hover:bg-teal-800 disabled:opacity-50">
                  Yes — add reservation
                </button>
                <button onClick={() => answer("not_yet")} disabled={busy} data-testid="dyb-not-yet"
                  className="px-3 py-1.5 border border-teal-600 text-teal-700 text-xs font-700 hover:bg-teal-100">Not yet</button>
                <button onClick={() => answer("no")} disabled={busy} data-testid="dyb-no"
                  className="px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-700">No</button>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-700 text-teal-900 mb-2">
                Save "{click.destination}" ({click.checkin || "?"} → {click.checkout || "?"}) to which trip?
              </div>
              <input value={conf} onChange={(e) => setConf(e.target.value)} placeholder="Confirmation # (optional)"
                className="border border-zinc-300 px-2.5 py-1.5 text-xs w-56 mb-2" data-testid="dyb-confirmation" />
              {trips === null ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <div className="flex flex-wrap gap-2">
                  {(trips || []).slice(0, 8).map((t) => (
                    <button key={t.id} onClick={() => saveTo(t.id)} data-testid={`dyb-trip-${t.id}`}
                      className="px-3 py-1.5 border border-teal-600 text-teal-700 text-xs font-700 hover:bg-teal-100">{t.title}</button>
                  ))}
                  {(trips || []).length === 0 && <span className="text-xs text-muted-foreground">No trips yet — create one in My Travel Hub.</span>}
                </div>
              )}
            </>
          )}
        </div>
        <button onClick={() => setClick(null)} className="text-zinc-400 hover:text-zinc-600" data-testid="dyb-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
